/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import { ADD_CONTENT, SELECT_CONTENT, SET_SHARED_CONTENT_PROPERTY, SET_STORY_PROPERTY,
    SET_QB_PROPERTY, SET_SELECTED_CONTENT_PROPERTY, ADD_OPTION, REMOVE_OPTION,
    UPDATE_OPTION_PROPERTY, SET_ACTIVE_SELECTION, SET_SELECTED_IMAGE, SET_CUSTOM_IMAGE,
    SET_PREVIEW_IMAGE, SELECT_CORRECT_OPTION, RECEIVE_CONTENT_DATA, RESET_CONTENT_STATUS,
    REORDER_CONTENTS, DELETE_CONTENT, INVALIDATE_SELECTED_CONTENT, UPDATE_NO_LIVE_CONTENT,
    SET_CONTENT_CHANGE_STATE, DISCARD_CONTENT_CHANGES, SET_CONTENT_TYPE, SET_CURRENT_UNIQUE_PROPERTY
} from 'state/actions/embedsState/embedManagementState/content_status';
import { SET_EMBED_CREATED_STATUS } from 'state/actions/embedsState/embedManagementState/selected_embed_status';

// Import methods to form content Status data structure
import { getDefaultState, formSingleContentData, formSingleOptionData } from 'state/reducers/embedsState/embedManagementState/contentStatus/content_status_state_structure';

// Import helper methods
import {
    fromContentsData, invalidateContent, generateId, getUpdatedStateParamsOnDelete,
    setSelectedImage, setPreviewImage, noLiveContentCheck, finalizeContentTypeChange,
    getStateWithOfflineContent, typeChangedFromOriginal, typeChangedToOriginal, seletedContentTypeChanged,
    removeOfflineBlob
} from 'state/reducers/embedsState/embedManagementState/contentStatus/content_status_helpers';


/* ========== ~~~~~~~~~~ EMBEDS STATE > EMBED MANAGEMENT STATE > CONTENT STATUS ~~~~~~~~~~ ========== */

// Store previously saved state. Used to discard changes;
let previousState = {};

// REDUCER
// used to manipulate application state
// takes an action, checks its type, based on that performs certain functionality, then returns new slice of state
// utilized in "_container" components

const contentStatus = (state = getDefaultState(), action) => {
    let newState = JSON.parse(JSON.stringify(state));
    switch (action.type) {
        case ADD_CONTENT:
            // check if the type of the previously selected content has changed and finalize the change
            if (seletedContentTypeChanged(newState.contents[newState.selectedContent])) newState = finalizeContentTypeChange(newState);

            // add new content
            const newContnentId = generateId();
            const quePosition = Object.keys(newState['contents']).length === 0 || newState.noLiveContents ? 0 :  newState['lastQueuePosition'] + 1;
            newState['contents'][newContnentId] = formSingleContentData({ contentData: null, contentProperties: null, contentId: newContnentId, type: action.contentType, position: quePosition });
            newState['lastQueuePosition'] = quePosition;
            newState['totalNumberOfContents'] +=1;
            newState['selectedContent'] = newContnentId;
            newState['activeSelection'] = 'background';
            newState['noLiveContents'] = false;
            newState['contentChangeDetected'] = true;
            newState['storyURLDataReceived'] = null;

            return newState;
        case SELECT_CONTENT:
            // check if the type of the previously selected content has changed and finalize the change
            if (seletedContentTypeChanged(newState.contents[action.contentId])) newState = finalizeContentTypeChange(newState);
            newState.selectedContent = action.contentId;
            return newState;
        case SET_CONTENT_TYPE:
            const originalType = newState['contents'][newState.selectedContent]['originalType'];
            const currentType = newState['contents'][newState.selectedContent]['contentType'];
            newState['contents'][newState.selectedContent]['contentType'] = action.contentType;
            newState['contentChangeDetected'] = true;
            if (typeChangedFromOriginal(originalType, currentType, action.contentType)) {
                // create offline blob
                newState = getStateWithOfflineContent(newState, previousState)
            } else if (typeChangedToOriginal(originalType, currentType, action.contentType)) {
                // remove offline blob
                newState = removeOfflineBlob(newState);
            }
            return newState;
        case SET_SELECTED_CONTENT_PROPERTY:
            if (action.isSharedProperty) {
                const contentType = newState['contents'][newState.selectedContent]['contentType'];
                const uniquePropertiesName = contentType === 'story' ? 'story' : 'questionBased';
                newState['contents'][newState.selectedContent][uniquePropertiesName][action.property] = action.value;
            } else {
                newState['contents'][newState.selectedContent][action.property] = action.value;
            }
            if (action.property === "type" && ['text1', 'text2'].includes(action.value) && newState['activeSelection'] !== 'background') {
                newState['activeSelection'] = 'none';
            }
            newState['contentChangeDetected'] = true;
            return newState;
        case SET_SHARED_CONTENT_PROPERTY:
            newState['contents'][action.contentId][action.property] = action.value;
            newState['contentChangeDetected'] = true;
            return newState;
        case SET_CURRENT_UNIQUE_PROPERTY:
            const currentContentType = newState['contents'][action.contentId]['contentType'] === 'story' ? 'story' : 'questionBased';
            newState['contents'][action.contentId][currentContentType][action.property] = action.value;
            newState['contentChangeDetected'] = true;
            return newState;
        case SET_QB_PROPERTY:
            newState['contents'][action.contentId]['questionBased'][action.property] = action.value;
            newState['contentChangeDetected'] = true;
            return newState;
        case SET_STORY_PROPERTY:
            newState['contents'][action.contentId]['story'][action.property] = action.value;
            newState['contentChangeDetected'] = true;
            return newState;
        case ADD_OPTION:
            const optionId = Object.keys(newState['contents'][action.contentId]['questionBased']['options']).length;
            newState['contents'][action.contentId]['questionBased']['options'][optionId] = formSingleOptionData(null, optionId);
            if (['image1', 'image2'].includes(newState['contents'][action.contentId]['questionBased']['type'])) {
                newState['activeSelection'] = `option_${optionId}`;
            }
            newState['contentChangeDetected'] = true;
            return newState;
        case REMOVE_OPTION:
            const newKeyedOptions = {};
            let newOptionId = 0;
            //if we delete current correctOption we will set correctOptionSelected back to false
            if(newState['contents'][action.contentId]['questionBased']['options'][action.optionId]['correctOption'] === true){
                newState['contents'][action.contentId]['questionBased']['correctOptionSelected'] = false
            }
            for (let [optionId, option] of Object.entries(newState['contents'][action.contentId]['questionBased']['options'])) {
                if (action.optionId !== optionId) {
                    newKeyedOptions[newOptionId] = option;
                    newOptionId += 1;
                }
            }
            newState['contents'][action.contentId]['questionBased']['options'] = JSON.parse(JSON.stringify(newKeyedOptions));
            if (newState['activeSelection'] !== 'background') { newState['activeSelection'] = 'none'}
            newState['contentChangeDetected'] = true;
            return newState;
        case UPDATE_OPTION_PROPERTY:
            newState['contents'][action.contentId]['questionBased']['options'][action.optionId][action.propertyName] = action.value;
            newState['contentChangeDetected'] = action.propertyName === 'isValid' ? newState['contentChangeDetected'] : true;
            return newState;
        case SET_ACTIVE_SELECTION:
            newState['activeSelection'] = action.selection;
            return newState;
        case SET_SELECTED_IMAGE:
            return setSelectedImage(newState, action);
        case SET_CUSTOM_IMAGE:
            const contentType = newState['contents'][action.contentId]['contentType'] === 'story' ? 'story' : 'questionBased';
            action.optionId === null ? newState['contents'][action.contentId][contentType]['backgroundImageURL'] = action.imageURL :
                newState['contents'][action.contentId]['questionBased']['options'][action.optionId]['imageURL'] = action.imageURL;
            newState['newUpload'] = action.imageURL;
            newState['contentChangeDetected'] = true;
            return newState;
        case SET_PREVIEW_IMAGE:
            return setPreviewImage(newState, action);
        case SELECT_CORRECT_OPTION:
            for (let option of Object.keys(newState['contents'][newState.selectedContent]['questionBased']['options'])) {
                newState['contents'][newState.selectedContent]['questionBased']['options'][option]['correctOption'] = `${option}` === `${action.optionId}`;
            }
            newState['contentChangeDetected'] = true;
            return newState;
        case RECEIVE_CONTENT_DATA:
            const formedData = fromContentsData(action.contentData.pages);
            newState['contents'] = formedData.contents;
            newState['lastQueuePosition'] = formedData.lastQueuePosition;
            newState['totalNumberOfContents'] = formedData.totalNumberOfContents;
            newState['firstLiveContentId'] = formedData.firstLiveContentId;
            newState['selectedContent'] = Object.keys(newState.contents).includes(newState.selectedContent) ? newState.selectedContent : formedData.firstLiveContentId;
            newState['noLiveContents'] = formedData.noLiveContents;
            newState['contentChangeDetected'] = false;
            previousState = JSON.parse(JSON.stringify(newState));
            return newState;
        case UPDATE_NO_LIVE_CONTENT:
            newState['noLiveContents'] = action.bool;
            return newState;
        case RESET_CONTENT_STATUS:
            newState = getDefaultState();
            previousState = JSON.parse(JSON.stringify(newState));
            if(!!action.setInitialContentId) newState['selectedContent'] = '0';
            return newState;
        case REORDER_CONTENTS:
            for (let idx=0; idx < action.liveContents.length; idx++) {
                let contentId = action.liveContents[idx].id;
                newState['contents'][contentId].queuePosition = idx;
                newState['contents'][contentId].contentLive = true;
            }
            for (let jdx=0; jdx < action.inactiveContents.length; jdx++) {
                let contentId = action.inactiveContents[jdx].id;
                newState['contents'][contentId].queuePosition = null;
                newState['contents'][contentId].contentLive = false;
            }
            newState['noLiveContents'] = action.liveContents.length === 0;
            newState['lastQueuePosition']  = action.liveContents.length -1;
            newState['contentChangeDetected'] = true;

            // check if the type of the previously selected content has changed and finalize the change
            if (seletedContentTypeChanged(newState.contents[newState.selectedContent])) newState = finalizeContentTypeChange(newState);
            return newState;
        case DELETE_CONTENT:
            const { [action.contentId]: _, ...updatedContents } = newState['contents'];
            const _prevContentPosition  = getUpdatedStateParamsOnDelete(updatedContents, action.contentId, newState['contents'][action.contentId].queuePosition);
            newState['contents'] = _prevContentPosition.contents;
            newState['lastQueuePosition'] = _prevContentPosition.lastQueuePosition;
            newState['selectedContent'] = newState['selectedContent'] === action.contentId ? _prevContentPosition.newSelectedContent : newState['selectedContent'];
            newState['totalNumberOfContents'] -= 1;
            newState['activeSelection'] = 'none';
            newState['noLiveContents'] = noLiveContentCheck(newState['contents']);
            newState['contentChangeDetected'] = true;
            return newState;
        case INVALIDATE_SELECTED_CONTENT:
            newState['contents'][newState.selectedContent] = invalidateContent(newState['contents'][newState.selectedContent]);
            return newState;
        case SET_CONTENT_CHANGE_STATE:
            newState['contentChangeDetected'] = action.contentChangeState;
            return newState;
        case DISCARD_CONTENT_CHANGES:
            return previousState;
        case SET_EMBED_CREATED_STATUS:
            newState['contentChangeDetected'] = false;
            previousState = JSON.parse(JSON.stringify(newState));
            return newState;
        default:
            return state;
    }
};



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default contentStatus;
